import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import ThemeContext from '../contexts/ThemeContext';
import i18n from '../i18n';
import { formatFileSize } from '../utils/formatters';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
  padding: 6px 0;
`;

const Track = styled.div`
  position: relative;
  width: 100%;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  background: ${({ $colors }) => $colors.hoverColor || 'rgba(69, 68, 107, 0.12)'};
`;

const Fill = styled.div`
  height: 100%;
  border-radius: 3px;
  width: ${({ $percent }) => $percent}%;
  background: ${({ $colors, $error }) => $error ? ($colors.errorColor || '#ea7e8f') : ($colors.tulkaMainColor || '#46446F')};
  transition: width 0.2s ease;
`;

const IndeterminateFill = styled.div`
  position: absolute;
  top: 0;
  left: -30%;
  height: 100%;
  width: 30%;
  border-radius: 3px;
  background: ${({ $colors }) => $colors.tulkaMainColor || '#46446F'};
  animation: transfer-indeterminate 1.2s ease-in-out infinite;

  @keyframes transfer-indeterminate {
    0% { left: -30%; }
    100% { left: 100%; }
  }
`;

const Meta = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: ${({ $colors, $error }) => $error ? ($colors.errorColor || '#ea7e8f') : ($colors.textSecondary || 'rgba(69, 68, 107, 0.6)')};
`;

const TransferProgressBar = ({ loaded, total, status, label }) => {
  const { colors } = useContext(ThemeContext);
  const isError = status === 'error';
  const determinate = typeof total === 'number' && total > 0;
  const percent = determinate ? Math.min(100, Math.round((loaded / total) * 100)) : null;

  return (
    <Wrapper data-test="transfer-progress-bar">
      <Track
        $colors={colors}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={determinate ? 100 : undefined}
        aria-valuenow={determinate ? percent : undefined}
      >
        {isError || determinate
          ? <Fill $colors={colors} $percent={isError ? 100 : percent} $error={isError} />
          : <IndeterminateFill $colors={colors} />}
      </Track>
      <Meta $colors={colors} $error={isError}>
        <span>{isError ? (label || i18n('transfer_failed')) : (label || '')}</span>
        <span>
          {isError
            ? ''
            : determinate
              ? `${percent}% · ${formatFileSize(loaded)} / ${formatFileSize(total)}`
              : formatFileSize(loaded)}
        </span>
      </Meta>
    </Wrapper>
  );
};

TransferProgressBar.propTypes = {
  loaded: PropTypes.number,
  total: PropTypes.number,
  status: PropTypes.oneOf(['active', 'done', 'error']),
  label: PropTypes.string,
};

TransferProgressBar.defaultProps = {
  loaded: 0,
  total: 0,
  status: 'active',
  label: undefined,
};

export default TransferProgressBar;
